import { ChangeEvent, ClipboardEvent, useRef } from 'react';

import { UKBankDetails } from 'types/graphql';

import useBankValidation from 'src/hooks/useBankValidation';
import { cleanInput, formatDashedInput } from 'src/lib/formatters';

import NPIDualInput, { MetaType } from '../NPIDualInput/NPIDualInput';
import { showErrorToast, showSuccessToast } from '../Toasts/toast';

import {
    getValidationCacheKey,
    getValidationResult,
    setValidationResult,
} from './SortCodeAccount.validation';

type SortCodeAccountInputProps = {
    values: { sortCode: string; accountNumber: string };
    setFieldValue: (field: string, value: string) => void;
    handleChange: (event: ChangeEvent<HTMLInputElement>) => void;
    setBankValidationResult: (result: UKBankDetails) => void;
    setValidatingBankDetails: (validating: boolean) => void;
    setLastSortCodeValue?: (value: string) => void;
    customFieldNames?: {
        sortCode?: string;
        additionalSortCode?: string;
        accountNumber?: string;
    };
};
const SortCodeAccountInput = ({
    values,
    setFieldValue,
    handleChange,
    setBankValidationResult,
    setValidatingBankDetails,
    setLastSortCodeValue,
    customFieldNames = {},
}: SortCodeAccountInputProps) => {
    const { onValidateUKBank, validatingUKBank } = useBankValidation();

    const inputRef = useRef<HTMLInputElement>(null);

    const handleInputChange = (
        event: ChangeEvent<HTMLInputElement>,
        setFieldValue: (field: string, value: string) => void,
        value: string
    ) => {
        const input = event.target.value;
        const selectionStart = event.target.selectionStart ?? input.length;

        const cleaned = cleanInput(input);
        const formatted = formatDashedInput(cleaned);

        const prev = value || '';

        // Estimate cursor movement
        let newCursor = selectionStart;

        const added = formatted.length > prev.length;
        const removed = formatted.length < prev.length;
        if (added && (formatted[selectionStart - 1] === '-' || formatted[selectionStart] === '-')) {
            newCursor++;
        } else if (removed && prev[selectionStart] === '-') {
            newCursor--;
        }

        setFieldValue(customFieldNames.sortCode ?? 'sortCode', formatted);
        if (customFieldNames?.additionalSortCode) {
            setFieldValue(customFieldNames.additionalSortCode, formatted);
        }
        if (setLastSortCodeValue) {
            setLastSortCodeValue(formatted);
        }

        // Adjust cursor position
        window.requestAnimationFrame(() => {
            const el = inputRef.current;
            if (el) {
                el.setSelectionRange(newCursor, newCursor);
            }
        });
    };

    const handlePaste = (
        event: ClipboardEvent<HTMLInputElement>,
        setFieldValue: (field: string, value: string) => void
    ) => {
        event.preventDefault();
        const pastedData = event.clipboardData.getData('Text');
        const cleaned = cleanInput(pastedData);
        const formattedValue = formatDashedInput(cleaned);
        setFieldValue(customFieldNames.sortCode ?? 'sortCode', formattedValue);
        if (customFieldNames?.additionalSortCode) {
            setFieldValue(customFieldNames.additionalSortCode, formattedValue);
        }
    };

    const validate = async (meta1: MetaType, meta2: MetaType, triggerState: 'click' | 'blur') => {
        setValidatingBankDetails(true);
        const { error: error1, value: sortCode } = meta1;
        const { error: error2, value: accountNumber } = meta2;
        const isValid = !error1 && !error2;

        if (!isValid) {
            showErrorToast(error1 || error2);
            setValidatingBankDetails(false);
            return;
        }

        const cacheKey = getValidationCacheKey(sortCode, accountNumber);
        const cached = getValidationResult(cacheKey);

        if (triggerState === 'blur' && cached?.wasManuallyValidated && cached.success) {
            setValidatingBankDetails(false);
            // Skip API call
            return;
        }

        await onValidateUKBank(
            { accountNumber, sortCode },
            (result) => {
                const success = ['OK', 'CautiousOK'].includes(result.StatusInformation);

                // Store result in cache
                setValidationResult(cacheKey, {
                    success,
                    wasManuallyValidated: triggerState === 'click',
                });

                if (!success) {
                    showErrorToast(
                        `Unable to Validate Bank Details due to ${result.StatusInformation}, please check the details again and validate.`
                    );
                } else {
                    showSuccessToast('Bank validation successful');
                }
                setValidatingBankDetails(validatingUKBank);
                setBankValidationResult(result);
            },
            (error) => {
                showErrorToast(error);
                setValidatingBankDetails(false);
            }
        );
    };

    const component1 = {
        name: customFieldNames.sortCode ?? 'sortCode',
        placeholder: 'Enter Sort Code',
        onChange: (event) => {
            return handleInputChange(event, setFieldValue, values.sortCode);
        },
        handlePaste: (event) => {
            return handlePaste(event, setFieldValue);
        },
        inputRef,
    };

    const component2 = {
        name: customFieldNames.accountNumber ?? 'accountNumber',
        placeholder: 'Enter Account Number',
        onChange: handleChange,
        inputProps: { maxLength: 8 },
    };

    return (
        <NPIDualInput
            title="Sort Code / Account Number"
            component1={component1}
            component2={component2}
            onValidate={({ meta1, meta2, triggerState }) => {
                validate(meta1, meta2, triggerState);
            }}
        />
    );
};

export default SortCodeAccountInput;
